import React, { Component } from "react";
import ComponentHeader from "./ComponentHeader";
import classnames from "classnames";
import styles from "./Components.local.scss";
import { TinyAreaChart } from "../../Common/Charts/TinyAreaChart";
import { number_format, abbreviateNumber, getLabelsByLng } from "Utils";
import _ from "i18n";
import ComponentActions from "./ComponentActions";
import { Modal } from "antd";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { CustomizedYAxisTick, CustomTooltip } from "../../Common/Charts/Common";

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#FF6384",
  "#36A2EB",
  "#FFCE56",
  "#4BC0C0",
  "#9966FF",
  "#FF9F40",
  "#FF4D4D",
  "#A4DE02",
  "#4B5000",
  "#1E90FF",
  "#FFD700",
  "#8B0000"
];

const CustomLabel = props => {
  const { x, y, width, value } = props;
  const offsetX = width / 2;
  const isNegative = value < 0;

  return (
    <text
      x={x + offsetX}
      y={isNegative ? y + 10 : y - 10}
      fill={isNegative ? "#fb185c" : "#2495e1"}
      textAnchor="middle"
      dominantBaseline="middle"
      style={{
        fontSize: "10px",
        fontWeight: "bold"
      }}
    >
      {`€ ${abbreviateNumber(value, 1)}`}
    </text>
  );
};

class SimpleValueAndTinyChartComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showModal: false,
      showSubModal: false,
      chartData: [],
      year: null
    };
  }

  processData = data => {
    return Object.keys(data).map(year => {
      const yearData = data[year];
      const mainValueKey = Object.keys(yearData).find(
        key => key !== "year" && key !== "children"
      );
      return {
        label: yearData.year,
        value: yearData[mainValueKey],
        children: yearData.children ? Object.values(yearData.children) : []
      };
    });
  };

  handleClick = () => {
    const {
      data: { data }
    } = this.props.component;

    this.setState({
      showModal: true,
      chartData: this.processData(data)
    });
  };

  handleClickBar = data => {
    this.setState({
      showSubModal: true,
      selectedData: data
    });
  };

  handleCloseModal = () => {
    this.setState({
      showModal: false,
      chartData: [],
      selectedData: [],
      year: null
    });
  };

  handleCloseSubModal = () => {
    this.setState({
      showSubModal: false,
      selectedData: []
    });
  };

  formatValue = value => {
    if (value === null || value === 0) {
      return "0";
    }
    return abbreviateNumber(value);
  };

  renderBarChartModal = (data, labels, showModal) => {
    return (
      <Modal
        title={`${getLabelsByLng(labels, "fr")} par période`}
        visible={showModal}
        onOk={this.handleCloseModal}
        onCancel={this.handleCloseModal}
        width={1200}
        bodyStyle={{ maxHeight: "90vh", overflowY: "auto" }}
      >
        <ResponsiveContainer width="100%" height={500}>
          <BarChart data={data} width={65}>
            <CartesianGrid
              vertical={false}
              stroke="#6d7f92"
              strokeWidth={0.2}
            />
            <XAxis dataKey="label" axisLine={false} />
            <YAxis
              tick={<CustomizedYAxisTick currency="€" />}
              axisLine={false}
              tickLine={false}
              type={"number"}
            />
            <Tooltip
              cursor={{ fill: "#f3faff", opacity: 0.6 }}
              content={<CustomTooltip />}
            />
            <Bar
              dataKey="value"
              type="monotone"
              barSize={55}
              fill="#2495E1"
              animationEasing={"ease-in-out"}
              animationDuration={800}
              minPointSize={2}
              cursor="pointer"
            >
              {Object.values(data).map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.value < 0 ? "#fb185c" : "#2495E1"}
                  onClick={() => this.handleClickBar(entry.children)}
                />
              ))}
              <LabelList
                dataKey="value"
                position="top"
                content={CustomLabel}
                formatter={this.formatValue}
              />
            </Bar>
            <ReferenceLine y={0} stroke="#00000080" strokeWidth={1} />
          </BarChart>
        </ResponsiveContainer>
      </Modal>
    );
  };

  renderChildrenChartSubModal = (data, showModal, handleCloseSubModal) => {
    const hasNegativeValues = data && data.some(d => d.value < 0);

    if (!data || data.length === 0) {
      return null;
    }

    const chartData = data.map(d => ({
      label: d.label,
      value: Math.abs(d.value)
    }));

    return (
      <Modal
        visible={showModal}
        width={1200}
        onOk={handleCloseSubModal}
        onCancel={handleCloseSubModal}
        style={{ marginTop: "50px" }}
        bodyStyle={{ maxHeight: "90vh", overflowY: "auto" }}
      >
        <ResponsiveContainer width="100%" height={500}>
          {hasNegativeValues ? (
            <BarChart data={data}>
              <XAxis dataKey="label" axisLine={false} />
              <YAxis
                tick={<CustomizedYAxisTick currency="€" />}
                axisLine={false}
                tickLine={false}
                type={"number"}
              />
              <Tooltip
                cursor={{ fill: "#f3faff", opacity: 0.6 }}
                formatter={value => (
                  <b style={{ color: value < 0 ? "#fb185c" : "#2495e1" }}>
                    <br /> {`${value} €`}
                  </b>
                )}
              />
              <Legend
                layout="horizontal"
                verticalAlign="top"
                align="center"
                height={100}
                payload={
                  chartData &&
                  chartData.map((entry, index) => ({
                    value: entry.label,
                    type: "square",
                    color: COLORS[index % COLORS.length]
                  }))
                }
              />
              <Bar
                dataKey="value"
                type="monotone"
                barSize={55}
                fill="#2495E1"
                animationEasing={"ease-in-out"}
                animationDuration={800}
                minPointSize={2}
                cursor="pointer"
              >
                {Object.values(data).map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
                <LabelList
                  dataKey="value"
                  position="top"
                  content={CustomLabel}
                  formatter={this.formatValue}
                />
              </Bar>
              <ReferenceLine y={0} stroke="#00000080" strokeWidth={1} />
            </BarChart>
          ) : (
            <PieChart>
              <Legend
                layout="horizontal"
                verticalAlign="top"
                align="center"
                payload={chartData.map((entry, index) => ({
                  value: entry.label,
                  type: "round",
                  color: COLORS[index % COLORS.length]
                }))}
              />
              <Tooltip
                cursor={{ fill: "#f3faff", opacity: 0.6 }}
                formatter={value => (
                  <b style={{ color: value < 0 ? "#fb185c" : "#2495e1" }}>
                    <br /> {`${value} €`}
                  </b>
                )}
              />
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="label"
                cx="50%"
                cy="50%"
                outerRadius={150}
                fill={COLORS[0]}
                minAngle={5}
                animationEasing={"ease-in-out"}
                animationDuration={800}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
            </PieChart>
          )}
        </ResponsiveContainer>
      </Modal>
    );
  };

  render() {
    const {
      slideId,
      isPreparePresentation,
      isComposition,
      component,
      isPresentation,
      hasComments,
      indicatorsExplanation,
      hasExplanation,
      layoutProps: { scaleComponent }
    } = this.props;

    const {
      componentId,
      labels,
      data: { account, data },
      config: { annexeAttachmentCategory }
    } = component;

    const {
      percentage,
      code,
      currentValue,
      diffValue,
      unit,
      notAvailable,
      isOneYearDashboard
    } = account[0];

    const { showModal, showSubModal, chartData, selectedData } = this.state;

    return (
      <React.Fragment>
        <div style={{ cursor: "pointer" }} onClick={this.handleClick}>
          <ComponentHeader
            slideId={slideId}
            percentage={percentage}
            title={labels}
            annexeAttachmentCategory={annexeAttachmentCategory}
            handleDisplayAnnexePDF={this.props.handleDisplayAnnexePDF}
            isComposition={isComposition}
            isPresentation={isPresentation}
            hasComments={hasComments}
            componentId={componentId}
            isPreparePresentation={isPreparePresentation}
            indicatorsExplanation={indicatorsExplanation}
            hasExplanation={hasExplanation}
            inSlideView={this.props.inSlideView}
          />
          <div
            className={classnames(
              styles.SimpleValueAndTinyChartComponent,
              styles.absolute_container,
              styles.flex_container,
              styles.align_container,
              scaleComponent ? styles.scale_component : null
            )}
          >
            {!this.props.inSlideView &&
              !this.props.isEmbedded && (
                <ComponentActions
                  removeComponent={this.props.handleRemoveComponent}
                  updateComponent={this.props.handleUpdateComponent}
                  componentCase={this.props.ComponentCase}
                  sideBar={"ComponentConfig"}
                  component={"SimpleValueAndTinyChartComponent.png"}
                />
              )}
            <div
              className={classnames(
                "medium-12 small-12",
                styles.flex_container,
                styles.align_container
              )}
            >
              <span
                className={classnames("medium-12 small-12", styles.main_value)}
              >
                {notAvailable
                  ? currentValue
                  : number_format(currentValue, 2, ",", " ")}{" "}
                {unit !== "none" ? unit : ""}
              </span>
              <span
                className={classnames(
                  "medium-12 small-12",
                  styles.sub_value,
                  percentage > 0 ? styles.positive_value : styles.negative_value
                )}
              >
                {notAvailable
                  ? _("notAvailable")
                  : percentage !== null
                    ? percentage > 0
                      ? _("increaseOf")
                      : _("decreaseOf")
                    : ""}{" "}
                {percentage ? (
                  <span className={styles.positive_value}>
                    {notAvailable ? "" : number_format(diffValue, 2, ",", " ")}{" "}
                    {unit !== "none" ? unit : ""}
                  </span>
                ) : (
                  ""
                )}
                {/*{isOneYearDashboard ? "" : <br />}*/}
                {/*{lastYear ? (*/}
                {/*  <span className={styles.year_info}>*/}
                {/*    de <b>{lastYear}</b> à <b>{currentYear}</b>*/}
                {/*  </span>*/}
                {/*) : currentYear ? (*/}
                {/*  <span className={styles.year_info}>*/}
                {/*    à l'année <b>{currentYear}</b>*/}
                {/*  </span>*/}
                {/*) : (*/}
                {/*  ""*/}
                {/*)}*/}
              </span>
            </div>

            {!isOneYearDashboard ? (
              <span
                className={classnames(
                  "medium-12 small-12",
                  styles.flex_container,
                  styles.align_container
                )}
              >
                <TinyAreaChart
                  chartData={Object.values(data)}
                  dataKeyY={code}
                  dataKeyX={"year"}
                  status={percentage > 0}
                />
              </span>
            ) : (
              ""
            )}
          </div>
        </div>
        {this.renderBarChartModal(chartData, labels, showModal)}
        {this.renderChildrenChartSubModal(
          selectedData,
          showSubModal,
          this.handleCloseSubModal
        )}
      </React.Fragment>
    );
  }
}

export default SimpleValueAndTinyChartComponent;



// WEBPACK FOOTER //
// ./src/components/Dashboard/Components/SimpleValueAndTinyChartComponent.js